import {
  formatIsoTzLess,
  getStartOfWeek,
  getStudyYearForDate,
  getWeek,
  WeekYear,
} from '../../date.utils';
import {
  CalendarNavigation,
  lastWeekOfYear,
  NavigationAction,
  NavigationActionType,
  ZoomFactor,
} from './useCalendarNavigation';
import { Dispatch } from 'react';

export const iterationKey = (week: WeekYear, iDay: number = 0) =>
  week.year * 1000 + week.week * 10 + iDay;

export const repeatIfDaily = <T>(
  isWeekly: boolean,
  fn: (iDay: number) => T,
) => {
  return (isWeekly ? [1] : [1, 2, 3, 4, 5, 6, 7]).map((i: number) => fn(i));
};

// Common functions (and previous static members) of uha/psa calendar

const headerWidth = 256; // Header on left side of the screen (emne/practice type)

const getWeeklyNumWeeks = () => {
  const cellWidth = 34;
  const initialAvailableWidth = window.innerWidth - headerWidth;
  return initialAvailableWidth / cellWidth;
};

const getDailyNumWeeks = () => {
  const cellWidth = 21;
  const initialAvailableWidth = window.innerWidth - headerWidth;
  const numDays = initialAvailableWidth / cellWidth;
  return numDays / 7;
};

interface WeekNumberProps {
  navigation: CalendarNavigation;
}

interface WeekNumberState {
  displayedYear: number;
  semester: 1 | 2;
}

export const getWeeksNumbersToRender = (
  displayedYear: number,
  semester: 1 | 2,
  weekOffset: number,
  zoomFactor: ZoomFactor,
): WeekYear[] => {
  return WeekYear.getWeeksArray(displayedYear, semester, weekOffset).slice(
    0,
    zoomFactor === ZoomFactor.WEEKLY ? getWeeklyNumWeeks() : getDailyNumWeeks(),
  );
};

export const updateNavigationByCurrentWeek = (
  props: WeekNumberProps,
  state: WeekNumberState,
  navigationDispatch: Dispatch<NavigationAction>,
): WeekYear[] => {
  const navigationState = props.navigation;
  const { displayedYear, semester } = state;
  const { zoomFactor, weekOffset } = navigationState;
  const now = new Date(Date.now());
  const fromDate = getStudyYearForDate(now)[0];
  const isCurrentYear = fromDate.getFullYear() === displayedYear;
  const currentWeek = getWeek(isCurrentYear ? now : fromDate);

  let weeks = getWeeksNumbersToRender(
    displayedYear,
    semester,
    weekOffset,
    zoomFactor,
  );

  if (weeks.length === 0) {
    return [];
  }

  let currentWeekIndex = weeks
    .map((weekyear) => weekyear.week)
    .indexOf(currentWeek);
  let middle = weeks.length / 2;

  if (navigationState.currentWeek < 0) {
    let currentWeekOffset = weekOffset;

    while (currentWeekIndex === -1 || currentWeekIndex > middle) {
      if (weeks[weeks.length - 1].week === lastWeekOfYear) {
        break;
      }

      currentWeekOffset += zoomFactor;
      weeks = getWeeksNumbersToRender(
        displayedYear,
        semester,
        currentWeekOffset,
        zoomFactor,
      );
      currentWeekIndex = weeks
        .map((weekyear) => weekyear.week)
        .indexOf(currentWeek);
      middle = weeks.length / 2;
    }

    if (currentWeekOffset !== weekOffset) {
      navigationDispatch({
        type: NavigationActionType.SET_OFFSET,
        payload: currentWeekOffset,
      });
    }

    if (currentWeek !== navigationState.currentWeek) {
      navigationDispatch({
        type: NavigationActionType.SET_CURRENT_WEEK,
        payload: currentWeek,
      });
    }
  }

  const lastWeekToRender = weeks[weeks.length - 1].week;

  if (
    lastWeekToRender &&
    lastWeekToRender !== navigationState.lastRenderedWeek
  ) {
    navigationDispatch({
      type: NavigationActionType.SET_LAST_RENDERED_WEEK,
      payload: lastWeekToRender,
    });
  }

  return weeks;
};

export const earliestVisibleDate = (weekNumbers: WeekYear[]) =>
  formatIsoTzLess(getStartOfWeek(weekNumbers[0]));

export const generateKey = (
  state: WeekNumberState,
  navigation: CalendarNavigation,
  filteredMode: boolean,
) => {
  return (
    `${state.displayedYear}` +
    `${state.semester}` +
    `${navigation.weekOffset}` +
    `${navigation.zoomFactor}` +
    `${filteredMode}` +
    `${navigation.currentWeek}` +
    `${navigation.forwardDisabled}` +
    `${navigation.backwardDisabled}` +
    `${navigation.lastRenderedWeek}`
  );
};

const defaultStartYear = (): number => {
  const date = new Date(Date.now());
  const isFallSemester = date.getMonth() >= 6;
  return isFallSemester ? date.getFullYear() : date.getFullYear() - 1;
};

export const getAndSetCurrentYearFromSessionStorage = (
  startYear?: string,
): number => {
  const startYearKey = 'calendarStartYear';
  const startYearFromStorage = sessionStorage.getItem(startYearKey);

  if (startYearFromStorage && !startYear) {
    const parsedFromStorage = parseInt(startYearFromStorage);

    return !isNaN(parsedFromStorage) ? parsedFromStorage : defaultStartYear();
  }

  const parsedStartYear = startYear ? parseInt(startYear) : NaN;
  const year = !isNaN(parsedStartYear) ? parsedStartYear : defaultStartYear();

  sessionStorage.setItem(startYearKey, year.toString());

  return year;
};
